import { useCallback, useMemo, useState } from 'react'
import { AddressType } from '@phantom/browser-sdk'
import { useConnect as usePhantomConnect, useIsExtensionInstalled, useModal, usePhantom } from '@phantom/react-sdk'
import { useAccount, useConnect as useWagmiConnect } from 'wagmi'
import { showApiErrorToast } from '@/lib/api-toast'
import type { WalletProvider } from '@/types/payment'

interface WalletProviderState {
	isConnected: boolean
	isConnecting: boolean
}

export function useWalletProviders() {
	const { isConnected: isMetaMaskConnected, connector } = useAccount()
	const { connectAsync, connectors, isPending: isMetaMaskConnecting } = useWagmiConnect()
	const { isConnected: isPhantomConnected, isConnecting: isPhantomConnecting, addresses } = usePhantom()
	const { connect: connectPhantom } = usePhantomConnect()
	const { open: openPhantomModal } = useModal()
	const { isInstalled: isPhantomExtensionInstalled, isLoading: isPhantomExtensionLoading } = useIsExtensionInstalled()
	const [connectingProviderId, setConnectingProviderId] = useState<WalletProvider | null>(null)

	const isMetaMaskLinked = isMetaMaskConnected && Boolean(connector?.id === 'metaMaskSDK' || connector?.name.toLowerCase().includes('metamask'))
	const isPhantomLinked = isPhantomConnected && Boolean(addresses.some((address) => address.addressType === AddressType.solana))

	const getProviderState = useCallback(
		(providerId: WalletProvider): WalletProviderState => {
			if (providerId === 'metamask') {
				return {
					isConnected: isMetaMaskLinked,
					isConnecting: isMetaMaskConnecting || connectingProviderId === 'metamask',
				}
			}

			if (providerId === 'phantom') {
				return {
					isConnected: isPhantomLinked,
					isConnecting: isPhantomConnecting || connectingProviderId === 'phantom',
				}
			}

			return {
				isConnected: false,
				isConnecting: false,
			}
		},
		[connectingProviderId, isMetaMaskConnecting, isMetaMaskLinked, isPhantomConnecting, isPhantomLinked],
	)

	const linkMetaMask = useCallback(async () => {
		const metaMaskConnector = connectors.find((item) => item.id === 'metaMask' || item.id === 'metaMaskSDK' || item.name.toLowerCase().includes('metamask')) ?? connectors[0]

		if (!metaMaskConnector) {
			throw new Error('MetaMask connector is not available.')
		}

		await connectAsync({ connector: metaMaskConnector })
	}, [connectAsync, connectors])

	const linkPhantom = useCallback(async () => {
		if (isPhantomExtensionLoading) {
			return
		}

		if (isPhantomExtensionInstalled) {
			await connectPhantom({ provider: 'injected' })
			return
		}

		openPhantomModal()
	}, [connectPhantom, isPhantomExtensionInstalled, isPhantomExtensionLoading, openPhantomModal])

	const linkProvider = useCallback(
		async (providerId: WalletProvider) => {
			const { isConnected, isConnecting } = getProviderState(providerId)

			if (isConnected || isConnecting || providerId === 'walletconnect') {
				return
			}

			setConnectingProviderId(providerId)

			try {
				if (providerId === 'metamask') {
					await linkMetaMask()
					return
				}

				if (providerId === 'phantom') {
					await linkPhantom()
				}
			} catch (error) {
				const message = error instanceof Error ? error.message : 'Unable to connect wallet.'
				showApiErrorToast(message)
			} finally {
				setConnectingProviderId(null)
			}
		},
		[getProviderState, linkMetaMask, linkPhantom],
	)

	const connectedProviderIds = useMemo(() => {
		const ids: WalletProvider[] = []

		if (isMetaMaskLinked) {
			ids.push('metamask')
		}

		if (isPhantomLinked) {
			ids.push('phantom')
		}

		return ids
	}, [isMetaMaskLinked, isPhantomLinked])

	return {
		connectedProviderIds,
		getProviderState,
		linkProvider,
	}
}
